export interface BaseStats {
  readonly hp: number;
  readonly atk: number;
  readonly def: number;
  readonly spd: number;
}

export interface Stats {
  readonly maxHp: number;
  readonly atk: number;
  readonly def: number;
  readonly spd: number;
}

export const MAX_LEVEL = 50;

/**
 * Bonus propres à chaque monstre (comme les IV de Pokémon) : tirés au hasard, de 0 à BONUS_MAX par
 * statistique, à l'apparition du monstre. Ils s'ajoutent aux statistiques de base de son espèce.
 */
export type StatBonus = BaseStats;
export const BONUS_MAX = 15;
export const NO_BONUS: StatBonus = { hp: 0, atk: 0, def: 0, spd: 0 };
export const STAT_KEYS = ['hp', 'atk', 'def', 'spd'] as const;
export const STAT_LABELS: Readonly<Record<(typeof STAT_KEYS)[number], string>> = { hp: 'PV', atk: 'Attaque', def: 'Défense', spd: 'Vitesse' };

/** Base de l'espèce + bonus du monstre : c'est de là que partent ses statistiques. */
export const withBonus = (base: BaseStats, bonus: StatBonus): BaseStats =>
  ({ hp: base.hp + bonus.hp, atk: base.atk + bonus.atk, def: base.def + bonus.def, spd: base.spd + bonus.spd });

/**
 * Tirage pondéré, pour éviter les monstres trop faibles : le poids d'une valeur croît linéairement de 1
 * (bonus 0) à `topWeight` (bonus maximal). Avec 4, un 15/15 sort 4 fois plus souvent qu'un 0/15 et le
 * bonus moyen passe de 7,5 (tirage uniforme) à 9,2 : 0,2 % de monstres 0★, 6,6 % 1★, 36 % 2★, 46,6 % 3★,
 * 10 % 4★ et 0,7 % 5★ (1 sur 10 000 parfait). Les chromatiques tirent avec un poids plus fort encore (8 : bonus moyen
 * 9,7, 13,6 % de 4★ et 1 % de 5★).
 */
export const BONUS_TOP_WEIGHT = 4;
export const SHINY_BONUS_TOP_WEIGHT = 8;
const BONUS_VALUES = Array.from({ length: BONUS_MAX + 1 }, (_, value) => value);
const bonusWeight = (value: number, topWeight: number) => 1 + ((topWeight - 1) * value) / BONUS_MAX;
const totalWeight = (topWeight: number) => BONUS_VALUES.reduce((sum, value) => sum + bonusWeight(value, topWeight), 0);

/** Probabilité de tirer exactement `value` pour une statistique. */
export const bonusChance = (value: number, topWeight = BONUS_TOP_WEIGHT): number =>
  (value < 0 || value > BONUS_MAX ? 0 : bonusWeight(value, topWeight) / totalWeight(topWeight));

/** Bonus d'un monstre qui apparaît ; `shiny` : tirage plus favorable encore. */
export const rollBonus = (random: () => number, shiny = false): StatBonus => {
  const topWeight = shiny ? SHINY_BONUS_TOP_WEIGHT : BONUS_TOP_WEIGHT;
  return { hp: roll(random, topWeight), atk: roll(random, topWeight), def: roll(random, topWeight), spd: roll(random, topWeight) };
};

function roll(random: () => number, topWeight: number): number {
  let remaining = random() * totalWeight(topWeight);
  for (const value of BONUS_VALUES) {
    remaining -= bonusWeight(value, topWeight);
    if (remaining < 0) return value;
  }
  return BONUS_MAX;
}

/** Bonus minimal, sur chaque stat, d'un monstre 5 étoiles. */
export const FIVE_STAR_BONUS = 13;

/**
 * Potentiel d'un monstre, de 0 à 5 étoiles : une étoile par cinquième de la somme de ses bonus (0 : un monstre faible,
 * moins de 3 de moyenne), jusqu'à 4 ; la 5e seulement s'il frôle le maximum partout (FIVE_STAR_BONUS sur chaque stat).
 */
export function bonusStars(bonus: StatBonus): number {
  const values = [bonus.hp, bonus.atk, bonus.def, bonus.spd];
  if (values.every((value) => value >= FIVE_STAR_BONUS)) return 5;
  const ratio = values.reduce((sum, value) => sum + value, 0) / (4 * BONUS_MAX);
  return Math.min(4, Math.floor(ratio * 5));
}

/** Bonus parfaits (le maximum partout) : ses 5 étoiles brillent. */
export const isPerfectBonus = (bonus: StatBonus): boolean => [bonus.hp, bonus.atk, bonus.def, bonus.spd].every((value) => value === BONUS_MAX);

/** Statistiques effectives d'un monstre à un niveau donné. */
export function computeStats(base: BaseStats, level: number): Stats {
  const scale = (value: number) => Math.floor((value * level) / 25);
  return {
    maxHp: scale(base.hp) + level + 10,
    atk: scale(base.atk) + 5,
    def: scale(base.def) + 5,
    spd: scale(base.spd) + 5,
  };
}

/** Expérience nécessaire pour passer du niveau `level` au suivant. */
export const xpToNextLevel = (level: number): number => 10 + level * level * 2;
